import { Component, OnDestroy, OnInit } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { LayoutService } from '@app/core/services/layout.service';
import { StateService } from '@app/core/services/state.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { GetProjectsParams, ProjectItem, ProjectService, ProjectsSettingsGet } from '@app/services/project.service';

type ProjectsStatus = 'ACTIVE' | 'NOTACTIVE' | 'ECONOMY';

@Component({
    selector: 'tt-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.css'],
    standalone: false,
})
export class ProjectsComponent implements OnInit, OnDestroy {
    public page?: ProjectsSettingsGet;
    public dateRange = { date_fom: '', date_tom: '', dateselector_index: 'day' };
    public projects: ProjectItem[] = [];
    public companies: BusinesscoItem[] = [];
    public grid?: GridOptions;
    public gridEconomy?: GridOptions;
    public readonly statusOptions: { id: ProjectsStatus; label: string }[] = [
        { id: 'ACTIVE', label: 'bg_projects_activestatus_active' },
        { id: 'NOTACTIVE', label: 'bg_projects_activestatus_notactive' },
        { id: 'ECONOMY', label: 'bg_projects_activestatus_economy' },
    ];

    private readonly tablet = this.layout.getMediaQueries().tablet;
    private readonly onTabletChanged = () => this.loadCurrentView();

    public get isTablet(): boolean {
        return this.tablet.matches;
    }

    constructor(
        private readonly projectService: ProjectService,
        private readonly logisticService: LogisticService,
        private readonly layout: LayoutService,
        private readonly state: StateService,
    ) {}

    public async ngOnInit(): Promise<void> {
        this.tablet.addEventListener('change', this.onTabletChanged);
        const params = this.state.getStateParams();
        const [page, companies] = await Promise.all([
            this.projectService.getProjectsSettings({ argtype: params['argtype'] ?? '0', argvalue: params['argvalue'] ?? '0' }),
            this.logisticService.getCompanies(),
        ]);
        this.page = page;
        this.dateRange = {
            date_fom: page.date_fom ?? '',
            date_tom: page.date_tom ?? '',
            dateselector_index: this.indexToInterval(page.dateselector_index),
        };
        this.companies = companies;
        await this.loadCurrentView();
    }

    public ngOnDestroy(): void {
        this.tablet.removeEventListener('change', this.onTabletChanged);
    }

    public async selectStatus(status: ProjectsStatus): Promise<void> {
        if (!this.page) return;
        this.page.bg_projects_activestatus = status;
        await this.loadCurrentView();
    }

    public async search(): Promise<void> {
        await this.loadCurrentView();
    }

    public async onSearchTextChanged(value: string): Promise<void> {
        if (this.page && value === '') await this.loadCurrentView();
    }

    public async onDateRangeChanged(value: { date_fom: string; date_tom: string; dateselector_index: string }): Promise<void> {
        if (!this.page) return;
        this.dateRange = value;
        this.page.date_fom = value.date_fom;
        this.page.date_tom = value.date_tom;
        this.page.dateselector_index = this.intervalToIndex(value.dateselector_index);
        this.page.date_fomtom = `${this.page.date_fom ?? ''}${this.page.date_tom ?? ''}`;
        await this.loadCurrentView();
    }

    public async onFilterChanged(): Promise<void> {
        await this.loadCurrentView();
    }

    public async loadProjects(): Promise<void> {
        if (!this.page) return;
        this.projects = await this.projectService.getProjects(this.page as GetProjectsParams);
    }

    public async loadCurrentView(): Promise<void> {
        if (!this.page) return;
        if (this.tablet.matches) {
            if (this.page.bg_projects_activestatus === 'ECONOMY') {
                this.gridEconomy = this.gridEconomy ?? this.createGrid('w_projects_economy_grid', 3482);
                this.gridEconomy.gridfunc?.read();
            } else {
                this.grid = this.grid ?? this.createGrid('w_projects_grid', 2748);
                this.grid.gridfunc?.read();
            }
        } else {
            await this.loadProjects();
        }
    }

    public searchProjectNames = ({ value }: { value: string | null }) => {
        if (!this.page) return [];
        return this.projectService.getProjects({
            ...this.page,
            bg_projects_activestatus: 'ALL',
            searchtext: value ?? '',
        });
    };

    public goToNewProject(): void {
        this.state.go('project', { project_keyno: '0' });
    }

    private createGrid(rememberId: string, method: number): GridOptions {
        const grid = new Grid()
            .setRememberId(rememberId)
            .setLoadDataFunction({ method, parameters: () => this.page })
            .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false });
        return rememberId === 'w_projects_grid' ? grid.setToolbar({ wrapping: true }) : grid;
    }

    private indexToInterval(index?: string | number): string {
        return ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'][Number(index) - 1] ?? 'day';
    }

    private intervalToIndex(interval?: string | number): string {
        if (typeof interval === 'number' || /^\d+$/.test(String(interval ?? ''))) return String(interval);
        const index = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'].indexOf(String(interval));
        return String(index < 0 ? 1 : index + 1);
    }
}
